import type {
  FdbAttribution,
  FdbCoPrescribedMedication,
  FdbInteractionMonograph,
  FdbMedication,
  FdbMedicationSearchResponse,
  FdbOverrideReasonCode,
  FdbRelease,
  FdbScreening,
  FdbScreeningFinding,
  FdbScreeningRequest,
} from "@emr/contracts";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import type { FdbOverrideChoice, PrescriptionFormValues } from "./PrescriptionFormFields";
import { structuredSigFromFormValues } from "./PrescriptionFormFields";

/**
 * FDB MedKnowledge coded prescribing: the medication picker under the
 * medication field and the screening panel of the Rx card. Screening here is
 * a preview; the signing RPC screens again and refuses alerts that need an
 * override reason and have none.
 */

export type FdbMedicationSearch = (
  query: string,
  signal?: AbortSignal,
) => Promise<FdbMedicationSearchResponse>;

export type FdbScreen = (
  encounterId: string,
  input: FdbScreeningRequest,
  signal?: AbortSignal,
) => Promise<FdbScreening>;

export type FdbMonograph = (
  monographId: number,
  signal?: AbortSignal,
) => Promise<FdbInteractionMonograph>;

export function useDebouncedValue<Value>(value: Value, delayMs: number): Value {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function tokens(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9.]+/)
    .filter((token) => token.length > 0);
}

/**
 * Orders search results by how many words of the typed (or AI-drafted)
 * medication they contain, so "Amoxicillin 500 mg capsule" puts the 500 mg
 * capsule first. The server search keys on the first word only.
 */
export function rankFdbMatches(
  medications: readonly FdbMedication[],
  typed: string,
): FdbMedication[] {
  const wanted = tokens(typed);
  const score = (medication: FdbMedication) => {
    const have = new Set(tokens(medication.display));
    return wanted.filter((token) => have.has(token)).length;
  };
  return [...medications].sort(
    (left, right) => score(right) - score(left) || left.display.localeCompare(right.display),
  );
}

export function FdbContentNotice({ release }: { readonly release: FdbRelease | null }) {
  if (release === null) return null;
  return release.contentStatus === "synthetic" ? (
    <p className="fdb-content-notice synthetic" role="note">
      Synthetic FDB-shaped test content ({release.buildDate}). Not for clinical use.
    </p>
  ) : (
    <p className="fdb-content-notice" role="note">
      FDB MedKnowledge Canada, {release.bundle}, build {release.buildDate}.
    </p>
  );
}

export function FdbAttributionNotice({
  attribution,
}: {
  readonly attribution: FdbAttribution | null | undefined;
}) {
  if (!attribution) return null;
  return (
    <p className="fdb-attribution">
      {attribution.disclaimer} {attribution.copyright}
      {attribution.drugProductDatabase ? ` ${attribution.drugProductDatabase}` : ""}
    </p>
  );
}

/** Prefer an exact identity; otherwise accept one database-verified generic equivalent. */
export function confidentFdbMatch(
  ranked: readonly FdbMedication[],
  drafted: string,
  allowSubstitution = true,
): FdbMedication | null {
  // Token overlap ranks suggestions, but cannot prove units, concentration, route or
  // release formulation. Normalize presentation only; leave incomplete labels uncoded.
  const normalized = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();
  const wanted = normalized(drafted);
  if (!wanted) return null;
  const exact = ranked.filter((medication) => normalized(medication.display) === wanted);
  if (exact.length > 0) return exact.length === 1 ? (exact[0] ?? null) : null;
  if (!allowSubstitution) return null;
  const equivalents = ranked.filter((medication) =>
    medication.genericEquivalentDisplays?.some((display) => normalized(display) === wanted),
  );
  return equivalents.length === 1 ? (equivalents[0] ?? null) : null;
}

function escapeRegExp(text: string): string {
  return text.replaceAll(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function HighlightedName({ text, typed }: { readonly text: string; readonly typed: string }) {
  const words = tokens(typed);
  if (words.length === 0) return <>{text}</>;
  const parts = text.split(new RegExp(`(${words.map(escapeRegExp).join("|")})`, "gi"));
  return (
    <>
      {parts.map((part, index) =>
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed split of an immutable string.
        index % 2 === 1 ? <mark key={index}>{part}</mark> : <Fragment key={index}>{part}</Fragment>,
      )}
    </>
  );
}

/**
 * The medication field with FDB MedKnowledge type-ahead. Typing lists FDB
 * products as you go (arrow keys and Enter pick one); picking codes the
 * prescription so it is screened. A medication the AI drafted is coded
 * silently when FDB has one clear match; the list only opens when the
 * clinician edits the field.
 */
export function FdbMedicationInput({
  search,
  values,
  onType,
  onPick,
  id,
  name,
  label,
  placeholder,
}: {
  readonly search: FdbMedicationSearch;
  readonly values: PrescriptionFormValues;
  readonly onType: (text: string) => void;
  /** "auto" when a drafted medication was coded without the clinician picking it. */
  readonly onPick: (
    medication: FdbMedication,
    attribution: FdbAttribution | null,
    how: "picked" | "auto",
  ) => void;
  readonly id: string;
  readonly name: string;
  readonly label: string;
  readonly placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const coded = values.medicationCode !== undefined;
  const drafted = values.medicationSource === "ai_draft";
  const text = values.medicationDisplay.trim();
  // Search the complete draft, including strength/form, so unrelated products do
  // not fill the result limit. Matching still checks the original complete label.
  const wanted = text.toLowerCase().slice(0, 120);
  const query = useDebouncedValue(wanted, drafted ? 0 : 200);
  const results = useQuery({
    queryKey: ["fdb-medication-search", query],
    queryFn: ({ signal }) => search(query, signal),
    enabled: !coded && query.length >= 2 && (drafted || open),
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });
  const matches = rankFdbMatches(results.data?.medications ?? [], values.medicationDisplay);
  const settled = results.data !== undefined && !results.isPlaceholderData && query === wanted;
  const autoMatch =
    drafted && !coded && settled && !results.isError && results.data?.hasMore === false
      ? confidentFdbMatch(matches, values.medicationDisplay, values.substitutionAllowed)
      : null;
  const attribution = results.data?.attribution ?? null;

  useEffect(() => {
    if (autoMatch !== null) onPick(autoMatch, attribution, "auto");
  }, [autoMatch, attribution, onPick]);

  const listId = `${id}-fdb-list`;
  const showList = open && !coded && query.length >= 2;
  const activeMatch = matches[Math.min(active, matches.length - 1)];
  const pick = (medication: FdbMedication) => {
    onPick(medication, attribution, "picked");
    setOpen(false);
  };

  return (
    <div className="fdb-typeahead">
      <input
        required
        id={id}
        name={name}
        autoComplete="off"
        role="combobox"
        // The surrounding label also holds the list and status text.
        aria-label={label}
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={
          showList && activeMatch !== undefined ? `${listId}-${activeMatch.code}` : undefined
        }
        value={values.medicationDisplay}
        placeholder={placeholder}
        onChange={(event) => {
          onType(event.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => {
          if (!coded && !drafted) setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
            const step = event.key === "ArrowDown" ? 1 : -1;
            setActive((index) =>
              Math.min(Math.max(index + step, 0), Math.max(matches.length - 1, 0)),
            );
          } else if (event.key === "Enter" && showList && activeMatch !== undefined) {
            event.preventDefault();
            pick(activeMatch);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {showList ? (
        <div className="fdb-typeahead-menu">
          {results.isError ? (
            <p role="status">
              FDB search is unavailable. This prescription can still be signed, without screening.
            </p>
          ) : results.data === undefined ? (
            <p role="status">Searching FDB…</p>
          ) : matches.length === 0 ? (
            <p role="status">No FDB match for “{text}”.</p>
          ) : (
            <div id={listId} role="listbox" aria-label="FDB matches">
              {matches.map((medication) => (
                <div
                  key={medication.code}
                  tabIndex={-1}
                  id={`${listId}-${medication.code}`}
                  role="option"
                  aria-selected={medication === activeMatch}
                  className={medication === activeMatch ? "active" : undefined}
                  // mousedown, not click: the input's blur would close the list first.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pick(medication);
                  }}
                  onMouseEnter={() => setActive(matches.indexOf(medication))}
                >
                  <span className="fdb-option-name">
                    <HighlightedName
                      text={medication.tallManDisplay ?? medication.display}
                      typed={values.medicationDisplay}
                    />
                  </span>
                  <span className="fdb-option-meta">
                    {medication.dins.length > 0 ? (
                      <span>DIN {medication.dins.join(", ")}</span>
                    ) : null}
                    {medication.strength ? (
                      <span>
                        {medication.strength} {medication.strengthUnit ?? ""}
                      </span>
                    ) : null}
                    <span>MEDID {medication.code}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {results.data !== undefined ? <FdbContentNotice release={results.data.release} /> : null}
        </div>
      ) : null}
      {coded ? (
        <p className="fdb-coded" role="status">
          <span className="fdb-badge">FDB</span> Coded as MEDID {values.medicationCode}.
          Interaction, allergy, duplicate therapy and dose checks run on this medication.
        </p>
      ) : drafted && !settled && !results.isError ? (
        <p className="fdb-uncoded" role="status">
          Matching to FDB…
        </p>
      ) : !showList && text.length >= 2 ? (
        <p className="fdb-uncoded" role="status">
          {drafted
            ? "No single FDB match for the drafted medication. Edit it to pick one, or sign it unscreened."
            : "Not coded: pick an FDB match to screen this prescription, or sign it unscreened."}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The screening request for the current form values, or null when uncoded or
 * incomplete. coPrescribed: the medications listed before this one on the same
 * prescription (RX-3), screened against it like chart medications.
 */
export function fdbScreeningRequestFromValues(
  values: PrescriptionFormValues,
  patientId: string,
  coPrescribed: readonly FdbCoPrescribedMedication[] = [],
): FdbScreeningRequest | null {
  if (values.medicationCode === undefined) return null;
  const structured = structuredSigFromFormValues(values);
  return {
    patientId,
    prescription: {
      medicationDisplay: values.medicationDisplay.trim(),
      medicationCode: values.medicationCode,
      medicationCodeSystem: "FDB_MEDID",
      ...(structured.ok && structured.sig !== null ? { structuredSig: structured.sig } : {}),
    },
    ...(coPrescribed.length === 0 ? {} : { coPrescribed: [...coPrescribed] }),
  };
}

/**
 * Screening of the coded medication in the form, refreshed as the dose — or a
 * medication listed before it on the same prescription — changes.
 */
export function useFdbScreening(args: {
  readonly screen: FdbScreen | undefined;
  readonly encounterId: string;
  readonly patientId: string;
  readonly values: PrescriptionFormValues;
  readonly coPrescribed?: readonly FdbCoPrescribedMedication[] | undefined;
}) {
  const request =
    args.screen === undefined
      ? null
      : fdbScreeningRequestFromValues(args.values, args.patientId, args.coPrescribed);
  const debounced = useDebouncedValue(request, 400);
  return useQuery({
    queryKey: ["fdb-screening", args.encounterId, debounced],
    queryFn: ({ signal }) =>
      (args.screen as FdbScreen)(args.encounterId, debounced as FdbScreeningRequest, signal),
    enabled: debounced !== null && args.screen !== undefined,
  });
}

/** Findings that need a reason before signing and do not have one yet. */
export function unansweredFindings(
  screening: FdbScreening | undefined,
  overrides: PrescriptionFormValues["overrides"],
): FdbScreeningFinding[] {
  if (screening === undefined) return [];
  return screening.findings.filter((finding) => {
    if (!finding.requiresOverride) return false;
    const choice = overrides?.[finding.key];
    return (
      choice === undefined ||
      (choice.reasonCode === "other" && (choice.reasonText ?? "").trim().length < 3)
    );
  });
}

const OVERRIDE_REASONS: readonly { value: FdbOverrideReasonCode; label: string }[] = [
  { value: "benefit_outweighs_risk", label: "Benefit outweighs the risk" },
  { value: "tolerated_previously", label: "Patient has tolerated it before" },
  { value: "monitoring_in_place", label: "Monitoring is in place" },
  { value: "dose_intended", label: "Dose is intended for this patient" },
  { value: "other", label: "Other (explain)" },
];

const SEVERITY_LABEL: Record<FdbScreeningFinding["severity"], string> = {
  contraindicated: "Contraindicated",
  severe: "Severe",
  moderate: "Moderate",
  undetermined: "Undetermined",
};

const UNSCREENED_REASON: Record<string, string> = {
  not_matched_to_fdb: "not matched to FDB",
  no_structured_sig: "enter a structured dose to check it",
  no_birth_date: "no date of birth on the chart",
  no_dosing_record: "no FDB dosing record for this patient",
  unit_not_convertible: "the dose unit cannot be compared",
  excluded_from_dose_checking: "FDB excludes it from dose checking",
};

function MonographLines({
  monographId,
  load,
}: {
  readonly monographId: number;
  readonly load: FdbMonograph;
}) {
  const monograph = useQuery({
    queryKey: ["fdb-monograph", monographId],
    queryFn: ({ signal }) => load(monographId, signal),
    staleTime: 30 * 60_000,
  });
  if (monograph.isPending) return <p role="status">Loading monograph…</p>;
  if (monograph.isError) return <p role="alert">The monograph could not be loaded.</p>;
  return (
    <div className="fdb-monograph">
      {monograph.data.lines.map((line, index) => (
        // Monograph lines have no identity beyond their order.
        // biome-ignore lint/suspicious/noArrayIndexKey: ordered, immutable monograph text.
        <p key={index}>{line.text}</p>
      ))}
      <FdbAttributionNotice attribution={monograph.data.attribution} />
    </div>
  );
}

function FindingRow({
  finding,
  choice,
  onChoose,
  loadMonograph,
  disabled,
}: {
  readonly finding: FdbScreeningFinding;
  readonly choice: FdbOverrideChoice | undefined;
  readonly onChoose: (choice: FdbOverrideChoice | undefined) => void;
  readonly loadMonograph: FdbMonograph | undefined;
  readonly disabled: boolean;
}) {
  const [showMonograph, setShowMonograph] = useState(false);
  const reasonId = `fdb-reason-${finding.key.replaceAll(/[^a-z0-9]+/gi, "-")}`;
  return (
    <li className={`fdb-finding ${finding.requiresOverride ? "blocking" : "warning"}`}>
      <p>
        <span className={`fdb-severity ${finding.severity}`}>
          {SEVERITY_LABEL[finding.severity]}
        </span>{" "}
        <strong>{finding.title}</strong>
      </p>
      <p>{finding.detail}</p>
      {finding.monographId !== undefined && loadMonograph !== undefined ? (
        <>
          <button
            type="button"
            className="ib-btn ghost sm"
            aria-expanded={showMonograph}
            onClick={() => setShowMonograph((open) => !open)}
          >
            {showMonograph ? "Hide monograph" : "Show monograph"}
          </button>
          {showMonograph ? (
            <MonographLines monographId={finding.monographId} load={loadMonograph} />
          ) : null}
        </>
      ) : null}
      {finding.requiresOverride ? (
        <div className="fdb-override">
          <label htmlFor={reasonId}>
            Override reason (required to sign)
            <select
              id={reasonId}
              disabled={disabled}
              value={choice?.reasonCode ?? ""}
              onChange={(event) =>
                onChoose(
                  event.target.value === ""
                    ? undefined
                    : {
                        reasonCode: event.target.value as FdbOverrideReasonCode,
                        reasonText: choice?.reasonText,
                      },
                )
              }
            >
              <option value="">Choose a reason…</option>
              {OVERRIDE_REASONS.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </label>
          {choice?.reasonCode === "other" ? (
            <label htmlFor={`${reasonId}-text`}>
              Explain
              <input
                id={`${reasonId}-text`}
                autoComplete="off"
                disabled={disabled}
                maxLength={500}
                value={choice.reasonText ?? ""}
                onChange={(event) => onChoose({ ...choice, reasonText: event.target.value })}
              />
            </label>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/** Alerts for the coded medication, with override reasons where signing needs them. */
export function FdbScreeningPanel({
  screening,
  isPending,
  isError,
  values,
  onChange,
  loadMonograph,
  disabled = false,
}: {
  readonly screening: FdbScreening | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly values: PrescriptionFormValues;
  readonly onChange: (values: PrescriptionFormValues) => void;
  readonly loadMonograph?: FdbMonograph | undefined;
  readonly disabled?: boolean;
}) {
  if (values.medicationCode === undefined) return null;
  if (isError) {
    return (
      <section className="fdb-screening" aria-label="Prescription screening">
        <p role="alert">
          Screening could not run. Signing screens again on the server and will refuse alerts that
          need a reason.
        </p>
      </section>
    );
  }
  if (screening === undefined || isPending) {
    return (
      <section className="fdb-screening" aria-label="Prescription screening">
        <p role="status">Screening against the chart…</p>
      </section>
    );
  }
  const choose = (key: string, choice: FdbOverrideChoice | undefined) => {
    const next = { ...(values.overrides ?? {}) };
    if (choice === undefined) delete next[key];
    else next[key] = choice;
    onChange({ ...values, overrides: next });
  };
  return (
    <section className="fdb-screening" aria-label="Prescription screening">
      <FdbContentNotice release={screening.release} />
      {screening.findings.length === 0 ? (
        <p role="status">No interaction, allergy, duplicate therapy or dose alerts.</p>
      ) : (
        <ul className="fdb-findings">
          {screening.findings.map((finding) => (
            <FindingRow
              key={finding.key}
              finding={finding}
              choice={values.overrides?.[finding.key]}
              onChoose={(choice) => choose(finding.key, choice)}
              loadMonograph={loadMonograph}
              disabled={disabled}
            />
          ))}
        </ul>
      )}
      {screening.unscreened.length > 0 ? (
        <div className="fdb-unscreened">
          <p>Not checked:</p>
          <ul>
            {screening.unscreened.map((item) => (
              <li key={`${item.kind}:${item.id ?? item.display}`}>
                {item.display} ({UNSCREENED_REASON[item.reason] ?? item.reason})
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <FdbAttributionNotice attribution={screening.attribution} />
    </section>
  );
}
